import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { X } from "react-feather";
import DataTable from "../../../../components/common/DataTable";
import ColumnVisibilityMenu from "../../../../components/common/ColumnVisibilityMenu";
import StatusBadge from "../../../../components/common/StatusBadge";
import ListExportToolbar from "../../components/list/ListExportToolbar";
import DateRangePicker, { useDatePresetRange } from "../../../../components/common/DateRangePicker";
import { useListStockMovementReportQuery } from "../../stockApi";
import { useGetProductGroupsQuery } from "../../../masters/products/productsApi";
import { useBranchFilter } from "../../../branchFilter/useBranchFilter";
import { paths } from "../../../../routes/paths";
import CustomerPicker from "../../../sales/components/CustomerPicker";
import ProductPicker from "../../components/ProductPicker";

const MOVEMENT_TYPE_OPTIONS = [
  { value: "", label: "All Types" },
  { value: "STOCK_IN", label: "Stock In" },
  { value: "STOCK_OUT", label: "Stock Out" },
  { value: "STOCK_RETURN", label: "Return" },
  { value: "STOCK_ADJUSTMENT", label: "Adjustment" },
  { value: "LPO", label: "LPO" },
];

const TYPE_BADGE = {
  STOCK_IN: { label: "Stock In", cls: "badge-linesuccess" },
  STOCK_OUT: { label: "Stock Out", cls: "badge-linedanger" },
  STOCK_RETURN: { label: "Return", cls: "badge-lineinfo" },
  STOCK_ADJUSTMENT: { label: "Adjustment", cls: "badge-linewarning" },
  LPO: { label: "LPO", cls: "badge-linesecondary" },
};

const money = (n) => (n === null || n === undefined ? "-" : Number(n).toFixed(2));

// Which of the four existing detail routes a row's DocumentRecordGuid opens
// - the report carries no route of its own (handoff Batch66: "no new detail
// endpoints needed", every document number drills into its real screen).
const detailPathFor = (row) => {
  switch (row.movementType) {
    case "STOCK_IN": return paths.stockInDetail(row.documentRecordGuid);
    case "STOCK_OUT": return paths.stockOutDetail(row.documentRecordGuid);
    case "STOCK_RETURN": return paths.stockReturnDetail(row.documentRecordGuid);
    case "STOCK_ADJUSTMENT": return paths.stockAdjustmentDetail(row.documentRecordGuid);
    case "LPO": return paths.stockLpoDetail(row.documentRecordGuid);
    default: return null;
  }
};

const documentSubLabel = (row) => {
  if (row.movementType === "STOCK_IN") return row.lpoNumber ? `against ${row.lpoNumber}` : "Direct purchase, no LPO";
  if (row.movementType === "STOCK_OUT") return row.issuedToType === "EMPLOYEE" ? "Issued to employee" : "Issued to customer";
  if (row.movementType === "STOCK_RETURN") return row.sourceStockOutNo ? `against ${row.sourceStockOutNo}` : "Direct to supplier";
  if (row.movementType === "STOCK_ADJUSTMENT") return row.approvedByName ? `Approved by ${row.approvedByName}` : null;
  if (row.movementType === "LPO") return row.customerName ? `For customer: ${row.customerName}` : row.movementReason || null;
  return null;
};

// Party/Reference: who or what this line is "about" - supplier for In/
// Return/LPO, employee-or-customer for Out, the customer an LPO was
// raised for takes priority over its supplier when both are present.
const partyLabel = (row) => {
  if (row.movementType === "STOCK_OUT") return (row.issuedToType === "EMPLOYEE" ? row.issuedToEmployeeName : row.issuedToCustomerName) || "-";
  if (row.movementType === "STOCK_ADJUSTMENT") return row.movementReason || "-";
  if (row.movementType === "LPO") return row.customerName || row.supplierName || "-";
  return row.supplierName || "-";
};

// Server-supported filters (dateFrom/dateTo/branchId/movementType/search/
// includeVoided take real query params, per Batch66_StockMovementReport.sql
// - unlike the other Stock lists, most of this page's filters go straight
// to the server. Party/Reference and Product Group are the two exceptions,
// both filtered client-side (see their own comments below) since neither
// maps cleanly onto a single server-side param for this report.
//
// Defaults to the trailing 30 days rather than "today" (the convention
// used on the transactional Stock lists) - this is a cross-cutting
// management report meant to show a trend, not a daily register, so a
// today-only default would render it almost empty out of the box. Date
// range itself is the shared preset panel (Today/This Week/This Month/etc,
// see components/common/DateRangePicker) - management feedback was that
// clicking out a custom day-by-day range every time was tedious; "Custom"
// still falls back to the two plain date inputs when a preset doesn't fit.
const StockMovementReportPage = () => {
  const navigate = useNavigate();
  const { selectedBranchId, availableBranches } = useBranchFilter();
  const { preset, setPreset, dateFrom, setDateFrom, dateTo, setDateTo } = useDatePresetRange("LAST_30_DAYS");
  const [productGroup, setProductGroup] = useState("");
  const [movementType, setMovementType] = useState("");
  // Party/Reference means different fields depending on movement type
  // (supplier for In/Return, employee/customer for Out, reason text for
  // Adjustment - see partyLabel below) - the server's own supplierSearch
  // param only matches SupplierName, so this filters client-side against
  // the exact same partyLabel() value the Party/Reference column shows,
  // over whatever's already been fetched (same pageSize:500 cap the KPIs
  // and isTruncated warning below already account for).
  const [partyFilter, setPartyFilter] = useState("");
  // Picker instead of free text (2026-09-04 ask) - CustomerPicker/
  // ProductPicker, same components LPO/Enquiry forms already use, reused
  // here so picking the one customer/product to filter by is a proper
  // search-and-select instead of typing and hoping it matches. Both search
  // endpoints now also accept the Reports.StockMovement permission (see
  // CustomersController/ProductsController) - a report viewer shouldn't
  // need Customers.View/Products.View just to filter their own report.
  //
  // Filters client-side against whatever's already fetched, same as
  // partyFilter above - NOT a server round-trip (@CustomerSearch dropped,
  // 2026-09-04 follow-up). Originally sent server-side so a customer's
  // movements outside the fetched pageSize:500 window would still be found -
  // but that same branchId scoping still applies to a server search too, so
  // a customer whose movements live in a branch other than the one
  // currently selected up top silently came back empty either way ("the
  // filter clears all data", reported against a real customer in a
  // different branch than the active filter) - a much more confusing
  // failure mode than the existing isTruncated warning already covers for
  // partyFilter's own client-side approach. Simpler and no less correct to
  // just match it the same way.
  const [selectedCustomerOption, setSelectedCustomerOption] = useState(null);
  const selectedCustomerName = selectedCustomerOption?.customer?.custName || "";
  // Products are branch-local (ProductPicker needs one real branchId, same
  // constraint as everywhere else it's used) - resets whenever the branch
  // filter changes since a product picked under one branch is meaningless
  // once that branch is no longer the scope.
  const [selectedProductOption, setSelectedProductOption] = useState(null);
  const selectedProductCode = selectedProductOption?.value ?? null;
  useEffect(() => {
    setSelectedProductOption(null);
  }, [selectedBranchId]);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [includeVoided, setIncludeVoided] = useState(false);
  // Summary = one row per document (Type/Date/Doc Number/Total Amount/
  // Status); Detail = the existing per-line view, unchanged default.
  const [viewMode, setViewMode] = useState("detail");
  // On-screen only - hiding a column here doesn't drop it from the PDF/
  // Excel export, which always carries the full set regardless.
  const [hiddenColumns, setHiddenColumns] = useState([]);
  const toggleColumn = (key) =>
    setHiddenColumns((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));

  useEffect(() => {
    const handle = setTimeout(() => setSearch(searchInput.trim()), 400);
    return () => clearTimeout(handle);
  }, [searchInput]);

  // Groups are branch-wise and Product Group Id collides across branches
  // (each branch has its own numeric id for e.g. "ALARMS") - with "All
  // branches" selected, listing every branch's own row showed the same
  // name twice (or more). Deduped by name for the dropdown (same fix as
  // Stock Balances' own group filter) and matched client-side against
  // productGroupName below rather than sent as a server-side productGroupId,
  // so picking "ALARMS" once actually covers every branch's ALARMS rows
  // instead of silently only the one branch whose id happened to get sent.
  const { data: allGroups = [] } = useGetProductGroupsQuery();
  const scopedGroups = selectedBranchId ? allGroups.filter((g) => g.branchId === selectedBranchId) : allGroups;
  const groupNames = [...new Set(scopedGroups.map((g) => g.groupName))].sort((a, b) => a.localeCompare(b));

  useEffect(() => {
    if (productGroup && !groupNames.includes(productGroup)) setProductGroup("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedBranchId]);

  const { data, isLoading } = useListStockMovementReportQuery({
    branchId: selectedBranchId ?? undefined,
    movementType: movementType || undefined,
    dateFrom: dateFrom || undefined,
    dateTo: dateTo || undefined,
    search: search || undefined,
    productCode: selectedProductCode ?? undefined,
    includeVoided,
    pageSize: 500,
  });

  const fetchedRows = data?.items || [];
  // "Movements" (below) stays tied to the server's own totalCount - it
  // reflects the date/branch/type/search filters the server actually
  // applies, same figure isTruncated warns against. Party/Group are
  // client-only filters layered on top for the table itself and the
  // per-type unit KPIs, deliberately not folded into that count.
  const totalCount = data?.totalCount ?? fetchedRows.length;
  const isTruncated = totalCount > fetchedRows.length;
  const rows = fetchedRows
    .filter((r) => !partyFilter.trim() || partyLabel(r).toLowerCase().includes(partyFilter.trim().toLowerCase()))
    .filter((r) => !selectedCustomerName || partyLabel(r).toLowerCase() === selectedCustomerName.toLowerCase())
    .filter((r) => !productGroup || r.productGroupName === productGroup);
  const branchCode = availableBranches.find((b) => b.branchId === selectedBranchId)?.branchCode;

  // Context-driven grouping (2026-09-04 ask): when this report was built by
  // picking a customer, group by Product Group instead of Type (what did
  // this customer take, broken down by category). Flipped the other way
  // when a product was picked/searched instead - group by Party/Reference
  // (who took it) since a product can span many different customers. Bare
  // "no filter at all" keeps the original Type grouping. `search` is the
  // one general-purpose box (placeholder: "Product, document no., party") -
  // a dedicated "product name" search doesn't exist separately, so a
  // non-empty general search is folded into the same "product" bucket as
  // ProductPicker below.
  //
  // The group heading itself still folds in the row's own actual full
  // customer/party name - partyLabel(r), real data - alongside the product
  // group: Product Group left, customer name right (listExportUtils.js's
  // GROUP_KEY_SPLIT "||"). Originally added because a typed customerSearch
  // was a partial/substring match that could span more than one real
  // customer; kept even now that selectedCustomerName is picked/exact,
  // since it's still useful confirmation and costs nothing.
  const groupByProductGroup = Boolean(selectedCustomerName);
  // A picked product (ProductPicker) counts as a "product search" the same
  // as the free-text box - either way it can span many different customers.
  const groupByCustomer = !groupByProductGroup && (Boolean(search) || Boolean(selectedProductCode));
  const exportGroupBy = groupByProductGroup
    ? (r) => `${r.productGroupName || "Ungrouped"}||${partyLabel(r)}`
    : groupByCustomer
    ? (r) => partyLabel(r)
    : (r) => TYPE_BADGE[r.movementType]?.label || r.movementType;

  // One row per document - Total Amount sums that document's own lines from
  // whatever's currently filtered/fetched (same pageSize:500 cap as the KPI
  // strip). Status/Date/Type/Party are consistent across a document's lines
  // (a document has exactly one supplier/customer/employee, it's a header-
  // level fact) - taken from the first line seen, no averaging/ambiguity.
  // productGroupName is the one genuinely per-line field here (a multi-line
  // document can span several groups) - also taken from the first line, as
  // a "mostly right" representative rather than a true per-document fact,
  // only used when grouping the export by Product Group (see
  // summaryExportGroupBy below).
  const summaryRows = useMemo(() => {
    const map = new Map();
    rows.forEach((r) => {
      const key = r.documentRecordGuid;
      const existing = map.get(key) || {
        documentRecordGuid: r.documentRecordGuid,
        movementType: r.movementType,
        documentDate: r.documentDate,
        documentNumber: r.documentNumber,
        status: r.status,
        isDeleted: r.isDeleted,
        totalAmount: 0,
        partyReference: partyLabel(r),
        productGroupName: r.productGroupName,
      };
      existing.totalAmount += Number(r.lineAmount || 0);
      map.set(key, existing);
    });
    return [...map.values()];
  }, [rows]);

  // KPI strip - computed from the fetched set (capped at pageSize:500, see
  // isTruncated above), not a separate summary endpoint (none exists).
  const stockInRows = rows.filter((r) => r.movementType === "STOCK_IN");
  const stockOutRows = rows.filter((r) => r.movementType === "STOCK_OUT");
  const returnInRows = rows.filter((r) => r.movementType === "STOCK_RETURN" && r.movementDirection === "IN");
  const adjustmentRows = rows.filter((r) => r.movementType === "STOCK_ADJUSTMENT");
  const stockInUnits = stockInRows.reduce((s, r) => s + Number(r.qty || 0), 0);
  const stockInAmount = stockInRows.reduce((s, r) => s + Number(r.lineAmount || 0), 0);
  const stockOutUnits = stockOutRows.reduce((s, r) => s + Number(r.qty || 0), 0);
  const returnedUnits = returnInRows.reduce((s, r) => s + Number(r.qty || 0), 0);
  const netAdjustmentUnits = adjustmentRows.reduce((s, r) => s + Number(r.signedQty || 0), 0);
  const pendingAdjustments = adjustmentRows.filter((r) => r.status === "SUBMITTED").length;

  // Exported separately from the on-screen columns (ALL_COLUMNS below),
  // which keep their own Type/Recorded By. Whichever field is currently the
  // *group key* is dropped from the column list - its own bold header row
  // already states it once, so repeating it down every row of the group is
  // the redundant one - and shown as a normal column otherwise:
  //  - Type is the default group (no customer/product picked) - dropped
  //    there, but a real column once grouping switches to Product Group or
  //    Party/Reference instead (missed on the first pass, 2026-09-04
  //    follow-up - Type was hard-coded off with no way to see it once it
  //    stopped being the group header).
  //  - Party/Reference is dropped whenever the group is Product Group or
  //    Party/Reference itself.
  // Recorded By was dropped from the export outright per management's ask,
  // unconditionally. This column-drop is export-only: the on-screen table
  // below keeps Party/Reference visible unconditionally instead - hiding it
  // there too caused real confusion, since there's no group-header
  // equivalent on screen to confirm *which* customer's data a customer
  // search actually pulled back.
  // Column order (2026-09-04 ask): Qty/Rate/Amount grouped together as one
  // contiguous numeric cluster, Document moved out to the very last column
  // instead of sitting in the middle of that cluster.
  const exportColumns = [
    ...(groupByProductGroup || groupByCustomer
      ? [{ header: "Type", value: (r) => TYPE_BADGE[r.movementType]?.label || r.movementType }]
      : []),
    { header: "Date", value: (r) => (r.documentDate ? new Date(r.documentDate).toLocaleDateString() : "-") },
    { header: "Product", value: (r) => `${r.productName} (${r.productCode})` },
    ...(groupByProductGroup || groupByCustomer ? [] : [{ header: "Party / Reference", value: (r) => partyLabel(r) }]),
    { header: "Branch", value: (r) => r.branchCode },
    { header: "Qty", value: (r) => Number(r.signedQty || 0).toFixed(2), align: "right" },
    { header: "Rate", value: (r) => money(r.unitRate), align: "right" },
    // Only ever real for an LPO row - App_LPOLine is the only source table
    // (of the five this report unions) that tracks per-line VAT at all
    // (Batch128_StockMovementReportVatAmount.sql). Blank, not 0.00, for
    // every other movement type - it's genuinely not tracked there, not a
    // zero-VAT line.
    { header: "VAT Amt", value: (r) => (r.vatAmount == null ? "-" : money(r.vatAmount)), align: "right" },
    { header: "Amount", value: (r) => money(r.lineAmount), align: "right" },
    { header: "Status", value: (r) => (r.isDeleted ? "Voided" : r.status) },
    { header: "Document", value: (r) => r.documentNumber },
  ];
  // PDF only - a printed page is usually one branch's own paperwork
  // already, so Branch is dropped there; Excel keeps it (raw-data format).
  const pdfExportColumns = exportColumns.filter((c) => c.header !== "Branch");

  // Summary export - same grouping/column-drop rule as Detail above.
  // Operates on summaryRows' own precomputed partyReference/productGroupName
  // rather than re-deriving from a raw line.
  const summaryExportGroupBy = groupByProductGroup
    ? (r) => `${r.productGroupName || "Ungrouped"}||${r.partyReference}`
    : groupByCustomer
    ? (r) => r.partyReference
    : (r) => TYPE_BADGE[r.movementType]?.label || r.movementType;
  const summaryExportColumns = [
    ...(groupByProductGroup || groupByCustomer
      ? [{ header: "Type", value: (r) => TYPE_BADGE[r.movementType]?.label || r.movementType }]
      : []),
    { header: "Date", value: (r) => (r.documentDate ? new Date(r.documentDate).toLocaleDateString() : "-") },
    ...(groupByProductGroup || groupByCustomer ? [] : [{ header: "Party / Reference", value: (r) => r.partyReference }]),
    { header: "Total Amount", value: (r) => money(r.totalAmount), align: "right" },
    { header: "Status", value: (r) => (r.isDeleted ? "Voided" : r.status) },
    { header: "Document", value: (r) => r.documentNumber },
  ];

  // key/label pairs feed both the per-header hide `x` and the Columns menu
  // (ColumnVisibilityMenu) - order here is the requested display order:
  // Type, Date, Product, Qty, Party/Reference, Branch, then the rest.
  const ALL_COLUMNS = [
    {
      key: "type",
      label: "Type",
      dataIndex: "movementType",
      render: (v) => {
        const meta = TYPE_BADGE[v] || { label: v, cls: "badge-linesecondary" };
        return <span className={`badge ${meta.cls}`}>{meta.label}</span>;
      },
    },
    { key: "date", label: "Date", dataIndex: "documentDate", render: (v) => (v ? new Date(v).toLocaleDateString() : "-") },
    {
      key: "product",
      label: "Product",
      dataIndex: "productName",
      render: (v, r) => (
        <>
          {v}
          <div className="text-muted small">{r.productCodeNew || r.productCode}</div>
        </>
      ),
    },
    {
      key: "qty",
      label: "Qty",
      dataIndex: "signedQty",
      render: (v, r) => {
        // LPO isn't a real stock movement yet (only the Stock-In raised
        // against it moves stock) - shown neutral/unsigned so it doesn't
        // read as an already-received quantity.
        if (r.movementType === "LPO") {
          return <span className="text-muted fw-semibold">{Number(v).toFixed(2)} {r.unit || ""} ordered</span>;
        }
        return (
          <span className={Number(v) < 0 ? "text-danger fw-semibold" : "text-success fw-semibold"}>
            {Number(v) > 0 ? "+" : ""}{Number(v).toFixed(2)} {r.unit || ""}
          </span>
        );
      },
    },
    {
      key: "party",
      label: "Party / Reference",
      dataIndex: "supplierName",
      render: (_, r) => partyLabel(r),
    },
    { key: "branch", label: "Branch", dataIndex: "branchCode" },
    {
      key: "document",
      label: "Document",
      dataIndex: "documentNumber",
      render: (v, r) => {
        const to = detailPathFor(r);
        const sub = documentSubLabel(r);
        return (
          <>
            {to ? <Link to={to}>{v}</Link> : v}
            {sub && <div className="text-muted small">{sub}</div>}
          </>
        );
      },
    },
    { key: "rate", label: "Rate", dataIndex: "unitRate", render: (v) => money(v) },
    { key: "amount", label: "Amount", dataIndex: "lineAmount", render: (v) => money(v) },
    {
      key: "status",
      label: "Status",
      dataIndex: "status",
      render: (v, r) =>
        r.isDeleted ? <span className="badge badge-linedanger">Voided</span> : <StatusBadge status={v} />,
    },
    {
      key: "recordedBy",
      label: "Recorded By",
      dataIndex: "createdByName",
      render: (v, r) => (
        <>
          {v || "-"}
          <div className="text-muted small">{r.createdDate ? new Date(r.createdDate).toLocaleString() : ""}</div>
        </>
      ),
    },
  ];

  // "Possibly in list view" (soft, per the ask) - just drop the column
  // outright when every visible row already shares the same customer;
  // unlike export there's no on-screen grouped-header to fall back on for
  // context, so this is the one case worth hiding here too. The product-
  // search/group-by-customer case is left alone on screen - a product
  // search can span many different customers, so Party/Reference is still
  // informative per row without a group header to convey it.
  const summaryColumns = [
    {
      title: "Type",
      dataIndex: "movementType",
      render: (v) => {
        const meta = TYPE_BADGE[v] || { label: v, cls: "badge-linesecondary" };
        return <span className={`badge ${meta.cls}`}>{meta.label}</span>;
      },
    },
    { title: "Date", dataIndex: "documentDate", render: (v) => (v ? new Date(v).toLocaleDateString() : "-") },
    {
      title: "Doc Number",
      dataIndex: "documentNumber",
      render: (v, r) => {
        const to = detailPathFor(r);
        return to ? <Link to={to}>{v}</Link> : v;
      },
    },
    // Always shown on screen, unlike the export (see exportColumns) - a
    // user flagged that hiding this the moment a customer search matched
    // made it impossible to visually confirm *which* customer's data was
    // actually showing, with no group-header equivalent on screen the way
    // export's grouped sections have.
    { title: "Party / Reference", dataIndex: "partyReference", render: (v) => v || "-" },
    { title: "Total Amount", dataIndex: "totalAmount", render: (v) => money(v) },
    {
      title: "Status",
      dataIndex: "status",
      render: (v, r) =>
        r.isDeleted ? <span className="badge badge-linedanger">Voided</span> : <StatusBadge status={v} />,
    },
  ];

  const columns = ALL_COLUMNS
    .filter((c) => !hiddenColumns.includes(c.key))
    .map(({ key, label, ...rest }) => ({
      ...rest,
      title: (
        <span className="d-inline-flex align-items-center gap-1">
          {label}
          <X
            size={12}
            className="text-muted"
            style={{ cursor: "pointer" }}
            title={`Hide ${label}`}
            onClick={() => toggleColumn(key)}
          />
        </span>
      ),
    }));

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="add-item d-flex">
            <div className="page-title">
              <h4>Stock Movement Report</h4>
              <h6>Every Stock-In, Stock-Out, Return, Adjustment and LPO line, in one drillable ledger</h6>
            </div>
          </div>
        </div>

        <div className="row mb-3">
          <div className="col-6 col-md">
            <div className="card"><div className="card-body py-2">
              <div className="text-muted small">Movements</div>
              <div className="fs-5 fw-bold">{totalCount}</div>
            </div></div>
          </div>
          <div className="col-6 col-md">
            <div className="card"><div className="card-body py-2">
              <div className="text-muted small">Stock In</div>
              <div className="fs-5 fw-bold text-success">{stockInUnits.toFixed(0)} units</div>
              <div className="text-muted small">{money(stockInAmount)} received</div>
            </div></div>
          </div>
          <div className="col-6 col-md">
            <div className="card"><div className="card-body py-2">
              <div className="text-muted small">Stock Out</div>
              <div className="fs-5 fw-bold text-danger">{stockOutUnits.toFixed(0)} units</div>
              <div className="text-muted small">Net of {returnedUnits.toFixed(0)} returned</div>
            </div></div>
          </div>
          <div className="col-6 col-md">
            <div className="card"><div className="card-body py-2">
              <div className="text-muted small">Net Adjustment</div>
              <div className="fs-5 fw-bold">{netAdjustmentUnits > 0 ? "+" : ""}{netAdjustmentUnits.toFixed(0)} units</div>
            </div></div>
          </div>
          <div className="col-6 col-md">
            <div className="card"><div className="card-body py-2">
              <div className="text-muted small">Pending Approval</div>
              <div className="fs-5 fw-bold text-warning">{pendingAdjustments}</div>
              <div className="text-muted small">adjustments</div>
            </div></div>
          </div>
        </div>

        <div className="card table-list-card">
          <div className="card-body">
            <div className="d-flex flex-column gap-2">
              <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                <DateRangePicker
                  preset={preset}
                  setPreset={setPreset}
                  dateFrom={dateFrom}
                  setDateFrom={setDateFrom}
                  dateTo={dateTo}
                  setDateTo={setDateTo}
                />
                <div className="btn-group" role="group">
                  <button
                    type="button"
                    className={`btn btn-sm ${viewMode === "detail" ? "btn-primary" : "btn-outline-primary"}`}
                    onClick={() => setViewMode("detail")}
                  >
                    Detail
                  </button>
                  <button
                    type="button"
                    className={`btn btn-sm ${viewMode === "summary" ? "btn-primary" : "btn-outline-primary"}`}
                    onClick={() => setViewMode("summary")}
                  >
                    Summary
                  </button>
                </div>
              </div>
              <div className="table-top flex-wrap gap-2" style={{ rowGap: 8 }}>
                <div className="search-set">
                  <div className="search-input">
                    <input
                      type="text"
                      placeholder="Product, document no., party"
                      className="form-control form-control-sm formsearch"
                      style={{ minWidth: 220, height: 34 }}
                      value={searchInput}
                      onChange={(e) => setSearchInput(e.target.value)}
                    />
                  </div>
                </div>
                <div className="d-flex gap-2 align-items-center flex-wrap">
                  <select className="form-control form-control-sm" style={{ width: 170, height: 34 }} value={movementType} onChange={(e) => setMovementType(e.target.value)}>
                    {MOVEMENT_TYPE_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                  <select className="form-control form-control-sm" style={{ width: 180, height: 34 }} value={productGroup} onChange={(e) => setProductGroup(e.target.value)}>
                    <option value="">All Groups</option>
                    {groupNames.map((name) => (
                      <option key={name} value={name}>{name}</option>
                    ))}
                  </select>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder="Party / Reference"
                    style={{ width: 180, height: 34 }}
                    value={partyFilter}
                    onChange={(e) => setPartyFilter(e.target.value)}
                  />
                  <div style={{ width: 220 }} title="Every movement for this customer, including LPOs raised for them">
                    <CustomerPicker branchId={selectedBranchId}
                      value={selectedCustomerOption}
                      onChange={setSelectedCustomerOption}
                      menuPortalTarget={document.body}
                      isClearable
                    />
                  </div>
                  <div style={{ width: 220 }} title={selectedBranchId ? "Every movement for this product" : "Select a branch first"}>
                    <ProductPicker
                      branchId={selectedBranchId}
                      value={selectedProductOption}
                      onChange={setSelectedProductOption}
                      isClearable
                    />
                  </div>
                </div>
                <div className="form-check d-flex align-items-center">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="movement-include-voided"
                    checked={includeVoided}
                    onChange={(e) => setIncludeVoided(e.target.checked)}
                  />
                  <label className="form-check-label" htmlFor="movement-include-voided">Include voided/deleted</label>
                </div>
                {viewMode === "detail" && (
                  <ColumnVisibilityMenu
                    columns={ALL_COLUMNS}
                    hidden={hiddenColumns}
                    onToggle={toggleColumn}
                  />
                )}
                <ListExportToolbar
                  title={`Stock Movement Report${viewMode === "summary" ? " (Summary)" : ""}`}
                  subtitleLines={[
                    `Branch: ${branchCode || "All"}`,
                    `Date: ${dateFrom} to ${dateTo}`,
                    // Now always a picked customer's own resolved full name
                    // (CustomerPicker, 2026-09-04), not typed free text - the
                    // "could be a substring match" caveat this used to carry
                    // no longer applies going forward. exportGroupBy's own
                    // per-row disambiguation stays regardless, in case two
                    // real customers happen to share the exact same name.
                    ...(selectedCustomerName ? [`Customer: ${selectedCustomerName}`] : []),
                    ...(selectedProductOption ? [`Product: ${selectedProductOption.label}`] : []),
                  ]}
                  branchCode={branchCode}
                  columns={viewMode === "summary" ? summaryExportColumns : exportColumns}
                  pdfColumns={viewMode === "summary" ? summaryExportColumns : pdfExportColumns}
                  rows={viewMode === "summary" ? summaryRows : rows}
                  fileName={viewMode === "summary" ? "StockMovementReport_Summary" : "StockMovementReport"}
                  groupBy={viewMode === "summary" ? summaryExportGroupBy : exportGroupBy}
                  groupTotals
                />
              </div>
            </div>
            {isTruncated && (
              <div className="alert alert-warning py-2 px-3 mb-2">
                Showing the first {rows.length} of {totalCount} matching lines - narrow the date range or filters for complete KPI totals.
              </div>
            )}
            <div className="table-responsive">
              {viewMode === "summary" ? (
                <DataTable
                  columns={summaryColumns}
                  dataSource={summaryRows}
                  rowKey="documentRecordGuid"
                  loading={isLoading}
                  onRow={(record) => {
                    const to = detailPathFor(record);
                    return to
                      ? { onClick: () => navigate(to), style: { cursor: "pointer" } }
                      : {};
                  }}
                />
              ) : (
                // documentRecordGuid is the header's guid, shared by every
                // line in the same document - not unique per row, so the key
                // needs the array index too.
                <DataTable
                  columns={columns}
                  dataSource={rows}
                  rowKey={(r, i) => `${r.documentRecordGuid}-${r.productCode}-${i}`}
                  loading={isLoading}
                  onRow={(record) => {
                    const to = detailPathFor(record);
                    return to
                      ? { onClick: () => navigate(to), style: { cursor: "pointer" } }
                      : {};
                  }}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StockMovementReportPage;
